// WebSocket から届くキャンバス共有タイマーの最新状態を保持する。
// revision が古い通知は破棄し、同時操作時の遅延到着で表示が巻き戻らないようにする。

import { create } from "zustand";
import {
  applyCanvasTimerCommand,
  createInitialCanvasTimerState,
  type CanvasTimerCommand,
  type CanvasTimerState,
} from "@refinement-canvas/domain";

interface PendingTimerCommand {
  readonly command: CanvasTimerCommand;
  readonly commandId: string;
  readonly createdAtEpochMs: number;
}

/** このタブで残り時間が 0 へ到達したことを表す一過性の終了通知。 */
export interface CanvasTimerFinishAlert {
  /** 終了を観測した表示状態の revision。表示状態が進んだら通知を取り下げる。 */
  readonly revision: number;
  /** 表示開始のローカル時刻。自動取り下げの起点にする。 */
  readonly startedAtEpochMs: number;
}

interface CanvasTimerStore {
  /** 最後にサーバから確定通知された状態。楽観更新のロールバック先として保持する。 */
  readonly authoritativeTimer: CanvasTimerState | undefined;
  /** 現在のキャンバスについて、サーバ権威の初期状態を一度でも受信したか。 */
  readonly hasReceivedInitialTimerSync: boolean;
  readonly pendingCommand: PendingTimerCommand | undefined;
  /** 終了音と同じ瞬間に立てる一過性の終了通知。未通知なら undefined。 */
  readonly finishAlert: CanvasTimerFinishAlert | undefined;
  /** 接続・再接続後の権威状態を受信するまで、共有操作を一時停止する。 */
  readonly beginTimerSync: () => void;
  /** サーバ時刻 - ローカル時刻。端末時計がずれていても共有残り時間を一致させる。 */
  readonly serverClockOffsetMs: number;
  readonly timer: CanvasTimerState | undefined;
  readonly applyOptimisticCommand: (params: {
    readonly command: CanvasTimerCommand;
    readonly commandId: string;
    readonly localNowEpochMs: number;
  }) => void;
  /** 対応する保留操作を権威状態へ戻し、実際に解消した場合は true を返す。 */
  readonly rejectCommand: (commandId: string) => boolean;
  readonly reset: () => void;
  readonly startFinishAlert: (alert: CanvasTimerFinishAlert) => void;
  readonly dismissFinishAlert: () => void;
  readonly setTimer: (params: {
    readonly commandId?: string | undefined;
    readonly localReceivedAtEpochMs: number;
    readonly serverNowEpochMs: number;
    readonly timer: CanvasTimerState;
  }) => void;
}

/** 表示状態が通知時の revision から進んだら、一過性の終了通知を取り下げる。 */
function keepFinishAlertForTimer(
  alert: CanvasTimerFinishAlert | undefined,
  nextTimer: CanvasTimerState | undefined
): CanvasTimerFinishAlert | undefined {
  if (!alert || !nextTimer) return alert;
  return nextTimer.revision === alert.revision ? alert : undefined;
}

export const useCanvasTimerStore = create<CanvasTimerStore>((set) => ({
  authoritativeTimer: undefined,
  finishAlert: undefined,
  hasReceivedInitialTimerSync: false,
  pendingCommand: undefined,
  serverClockOffsetMs: 0,
  timer: undefined,
  beginTimerSync: () => set({ hasReceivedInitialTimerSync: false }),
  applyOptimisticCommand: ({ command, commandId, localNowEpochMs }) =>
    set((state) => {
      const baseTimer = state.timer ?? createInitialCanvasTimerState();
      const serverNowEpochMs = localNowEpochMs + state.serverClockOffsetMs;
      const nextTimer = applyCanvasTimerCommand(baseTimer, command, serverNowEpochMs);
      return {
        finishAlert: keepFinishAlertForTimer(state.finishAlert, nextTimer),
        pendingCommand: { command, commandId, createdAtEpochMs: localNowEpochMs },
        timer: nextTimer,
      };
    }),
  rejectCommand: (commandId) => {
    let rejected = false;
    set((state) => {
      if (state.pendingCommand?.commandId !== commandId) return state;
      rejected = true;
      return {
        finishAlert: keepFinishAlertForTimer(state.finishAlert, state.authoritativeTimer),
        pendingCommand: undefined,
        timer: state.authoritativeTimer,
      };
    });
    return rejected;
  },
  reset: () =>
    set({
      authoritativeTimer: undefined,
      finishAlert: undefined,
      hasReceivedInitialTimerSync: false,
      pendingCommand: undefined,
      serverClockOffsetMs: 0,
      timer: undefined,
    }),
  startFinishAlert: (alert) => set({ finishAlert: alert }),
  dismissFinishAlert: () => set({ finishAlert: undefined }),
  setTimer: ({ commandId, localReceivedAtEpochMs, serverNowEpochMs, timer }) =>
    set((state) => {
      const acknowledgesPendingCommand =
        commandId !== undefined && commandId === state.pendingCommand?.commandId;
      // timer.request の応答は、サーバが適用済みと確認した commandId のみを返す。
      // 相関しない権威応答では、後から始まった別操作の保留を解消しない。
      const clearsPendingCommand = acknowledgesPendingCommand;
      if (state.authoritativeTimer && state.authoritativeTimer.revision > timer.revision) {
        // 古い状態で表示を巻き戻さず、遅れて届いた自己 ACK だけは操作中状態の解消に利用する。
        if (clearsPendingCommand) {
          return {
            finishAlert: keepFinishAlertForTimer(state.finishAlert, state.authoritativeTimer),
            hasReceivedInitialTimerSync: true,
            pendingCommand: undefined,
            timer: state.authoritativeTimer,
          };
        }
        if (!state.hasReceivedInitialTimerSync) {
          return { hasReceivedInitialTimerSync: true };
        }
        return state;
      }

      const serverClockOffsetMs = serverNowEpochMs - localReceivedAtEpochMs;
      const pendingCommand = clearsPendingCommand ? undefined : state.pendingCommand;
      const visibleTimer = pendingCommand
        ? applyCanvasTimerCommand(timer, pendingCommand.command, serverNowEpochMs)
        : timer;
      return {
        authoritativeTimer: timer,
        finishAlert: keepFinishAlertForTimer(state.finishAlert, visibleTimer),
        hasReceivedInitialTimerSync: true,
        pendingCommand,
        serverClockOffsetMs,
        timer: visibleTimer,
      };
    }),
}));
